import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { FC, useState } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';

interface CanvasSizeModalProps {
	open: boolean;
	onConfirm: (width: number, height: number) => void;
}

const CanvasSizeModal: FC<CanvasSizeModalProps> = ({ open, onConfirm }) => {
	const [dimensions, setDimensions] = useState({ width: '100', height: '100' });
	const [errors, setErrors] = useState({ width: '', height: '' });

	const validateDimensions = (width: string, height: string, showEmptyErrors = true) => {
		const widthNum = parseInt(width);
		const heightNum = parseInt(height);
		const newErrors = { width: '', height: '' };

		if (!width || isNaN(widthNum)) {
			if (showEmptyErrors) newErrors.width = 'Width is required';
		} else if (widthNum < 10) {
			newErrors.width = 'Width must be at least 10';
		} else if (widthNum > 300) {
			newErrors.width = 'Width must be less than 300';
		}

		if (!height || isNaN(heightNum)) {
			if (showEmptyErrors) newErrors.height = 'Height is required';
		} else if (heightNum < 10) {
			newErrors.height = 'Height must be at least 10';
		} else if (heightNum > 300) {
			newErrors.height = 'Height must be less than 300';
		}

		setErrors(newErrors);
		return newErrors.width === '' && newErrors.height === '';
	};

	const handleWidthChange = (value: string) => {
		setDimensions(prev => ({ ...prev, width: value }));
		// Validate immediately on change for better UX
		validateDimensions(value, dimensions.height, false);
	};

	const handleHeightChange = (value: string) => {
		setDimensions(prev => ({ ...prev, height: value }));
		// Validate immediately on change for better UX
		validateDimensions(dimensions.width, value, false);
	};

	const handleWidthBlur = () => {
		if (dimensions.width) {
			validateDimensions(dimensions.width, dimensions.height, false);
		}
	};

	const handleHeightBlur = () => {
		if (dimensions.height) {
			validateDimensions(dimensions.width, dimensions.height, false);
		}
	};

	const handleConfirm = () => {
		const isValid = validateDimensions(dimensions.width, dimensions.height);
		if (isValid) {
			onConfirm(parseInt(dimensions.width), parseInt(dimensions.height));
		}
	};

	// const setPresetSize = (width: number, height: number) => {
	// 	setDimensions({ width: width.toString(), height: height.toString() });
	// 	// Clear any existing errors
	// 	setErrors({ width: '', height: '' });
	// };

	return (
		<Modal open={open} aria-labelledby="canvas-size-title" aria-describedby="canvas-size-description">
			<Box
				sx={{
					position: 'absolute' as 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: '90%',
					maxWidth: '400px',
					border: '1px solid #000',
					boxShadow: 24,
					borderRadius: 3,
					p: 3,
				}}
			>
				<Typography id="canvas-size-title" variant="h6" component="h2" align="center" mb={1}>
					Select Canvas Size
				</Typography>
				<Typography variant="body2" align="center" color="text.secondary" mb={2}>
					Choose the dimensions for your pixel art canvas (10-300 pixels)
				</Typography>
				<Divider variant="middle" light />

				{/* Preset Size Buttons */}
				{/* <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', my: 2, flexWrap: 'wrap' }}>
					<Button variant="outlined" size="small" onClick={() => setPresetSize(50, 50)}>
						50x50
					</Button>
					<Button variant="outlined" size="small" onClick={() => setPresetSize(100, 100)}>
						100x100
					</Button>
					<Button variant="outlined" size="small" onClick={() => setPresetSize(200, 200)}>
						200x200
					</Button>
					<Button variant="outlined" size="small" onClick={() => setPresetSize(300, 300)}>
						300x300
					</Button>
				</Box> */}

				{/* Custom Size Inputs */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						margin: '20px 0',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, justifyContent: 'center' }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<OutlinedInput placeholder="Width" value={dimensions.width} onChange={e => handleWidthChange(e.target.value)} onBlur={handleWidthBlur} type="number" sx={{ width: '80px', mb: 0.5 }} error={!!errors.width} />
							<Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
								width
							</Typography>
						</Box>
						<Typography variant="h5" px={2} sx={{ mt: 2 }}>
							×
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<OutlinedInput
								placeholder="Height"
								value={dimensions.height}
								onChange={e => handleHeightChange(e.target.value)}
								onBlur={handleHeightBlur}
								type="number"
								sx={{ width: '80px', mb: 0.5 }}
								error={!!errors.height}
							/>
							<Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
								height
							</Typography>
						</Box>
					</Box>

					{/* Error Messages */}
					{(errors.width || errors.height) && (
						<Box sx={{ textAlign: 'center', mb: 1 }}>
							{errors.width && (
								<Typography variant="caption" color="error" display="block">
									{errors.width}
								</Typography>
							)}
							{errors.height && (
								<Typography variant="caption" color="error" display="block">
									{errors.height}
								</Typography>
							)}
						</Box>
					)}
				</Box>

				<Typography variant="body2" align="center" color="text.secondary" mb={2}>
					{dimensions.width && dimensions.height ? `${dimensions.width} × ${dimensions.height} pixels` : 'Enter canvas dimensions above'}
				</Typography>

				<Button variant="contained" fullWidth onClick={handleConfirm} sx={{ mt: 2 }} disabled={!dimensions.width || !dimensions.height || !!errors.width || !!errors.height}>
					Create Canvas
				</Button>
			</Box>
		</Modal>
	);
};

export default CanvasSizeModal;
